import { useMemo } from "react";
import {
  type BookedFlightItineraryWithDepartureTime,
  ScheduleChangeSeverity,
} from "@b2bportal/air-booking-api";
import { getScheduleChangeDiffs } from "@b2bportal/core-utilities";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";

export const useScheduleChange = (
  itinerary: BookedFlightItineraryWithDepartureTime
) => {
  const { formatDateTime, t } = useI18nContext();

  return useMemo(() => {
    const {
      scheduleChange,
      travelItinerary: { slices },
    } = itinerary.bookedItinerary;
    const diffModel = getScheduleChangeDiffs(slices, scheduleChange);

    if (
      scheduleChange == null ||
      scheduleChange.severity === ScheduleChangeSeverity.Unknown
    ) {
      return null;
    }

    return {
      context: {
        diffModel,
        scheduleChange,
        expDate: formatDateTime(
          new Date(scheduleChange.expiry),
          DateTimeFormatStyle.ShortMonthDayShortWeekdayTime
        ),
        hasMajorChange:
          scheduleChange.severity === ScheduleChangeSeverity.Major,
        hasMinorChange:
          scheduleChange.severity === ScheduleChangeSeverity.Minor,
      },
      copy: {
        airlineWarning: t(
          "core-flights.itinerary.card.notificationBanner.airlineWarning"
        ),
        /** This copy has embedded html. Use `<Trans />`  */
        majorLabelKey:
          "core-flights.itinerary.card.notificationBanner.majorLabel",
        /** This copy has embedded html. Use `<Trans />`  */
        minorLabelKey:
          "core-flights.itinerary.card.notificationBanner.minorLabel",
        scheduleChangeCtaLabel: t(
          "core-flights.itinerary.card.notificationBanner.reviewScheduleChangeBtn"
        ),
      },
    };
  }, [formatDateTime, itinerary, t]);
};

export type ScheduleChangeData = ReturnType<typeof useScheduleChange>;
